import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { FileSignature, Printer, Download, Trash2, Eye, X, Sparkles, ShieldAlert } from "lucide-react";

const fd = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : "";

function buildHtml(f, lh = {}) {
  const duties = (f.perks || "").split(/\n|;/).map((p) => p.trim()).filter(Boolean).map((p) => `<li style="margin:4px 0">${p}</li>`).join("");
  const contact = [lh.phone, lh.email].filter(Boolean).join(" · ");
  const head = (lh.logo_data || lh.address || lh.company_name)
    ? `<table style="width:100%;border-bottom:2px solid #1a1a1a;padding-bottom:12px;margin-bottom:20px"><tr>
        <td>${lh.logo_data ? `<img src="${lh.logo_data}" style="max-height:64px;max-width:200px"/>` : ""}</td>
        <td style="text-align:right;font-size:13px;color:#444;line-height:1.5"><b style="font-size:16px;color:#1a1a1a">${lh.company_name || f.company_name || ""}</b><br/>${(lh.address || "").replace(/\n/g, "<br/>")}${contact ? `<br/>${contact}` : ""}</td>
      </tr></table>` : `<h2 style="margin:0 0 4px 0">${f.company_name || ""}</h2>`;
  return `<div style="font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;line-height:1.7;max-width:720px">
    ${head}
    <p style="text-align:right;color:#555">${fd(new Date().toISOString())}</p>
    <h3 style="text-align:center;text-decoration:underline;margin:16px 0">TO WHOMSOEVER IT MAY CONCERN — EXPERIENCE CERTIFICATE</h3>
    <p>This is to certify that <b>${f.candidate_name}</b>${f.employee_id ? ` (Employee ID: <b>${f.employee_id}</b>)` : ""} was employed with <b>${f.company_name}</b> as <b>${f.job_title}</b> from <b>${fd(f.start_date)}</b> to <b>${fd(f.end_date)}</b>.</p>
    ${duties ? `<p>During this tenure, key responsibilities and achievements included:</p><ul style="padding-left:20px">${duties}</ul>` : ""}
    <p>Throughout the period of employment, we found ${(f.candidate_name || "").split(" ")[0]} to be sincere, hardworking and professional. Conduct and performance were found to be satisfactory, and the association with the company was valued.</p>
    <p>We wish ${(f.candidate_name || "").split(" ")[0]} continued success in all future endeavours.</p>
    <p style="margin-top:32px">Sincerely,</p>
    <p style="margin:24px 0 0 0"><b>HR Team</b><br/>${f.company_name || ""}</p>
    <div style="margin-top:56px"><table style="width:100%;font-size:14px"><tr><td>Authorised Signatory: ______________________</td><td style="text-align:right">Company Seal</td></tr></table></div>
  </div>`;
}

function printL(html, title) {
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>${title}</title></head><body style="padding:40px">${html}</body></html>`);
  w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
}
function downloadL(html, title) {
  const blob = new Blob([`<html><head><meta charset="utf-8"></head><body>${html}</body></html>`], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${title}.doc`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

const EMPTY = { candidate_name: "", employee_id: "", job_title: "", company_name: "HCIT", start_date: "", end_date: "", perks: "" };

export default function ExperienceLetters() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [history, setHistory] = useState([]);
  const [lh, setLh] = useState({});
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/offer-letters").then((r) => setHistory(r.data.filter((x) => x.letter_type === "experience"))).catch((e) => setErr(formatApiError(e)));
  useEffect(() => {
    load();
    api.get("/settings/letterhead").then((r) => {
      setLh(r.data);
      if (r.data.company_name) setForm((f) => ({ ...f, company_name: r.data.company_name }));
    }).catch(() => {});
  }, []);

  const generate = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const html = buildHtml(form, lh);
      await api.post("/offer-letters", { ...form, response_deadline: null, letter_type: "experience", letter_html: html });
      setPreview({ title: `Experience Letter - ${form.candidate_name}`, html });
      setForm({ ...EMPTY, company_name: lh.company_name || EMPTY.company_name });
      await load();
    } catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete experience letter for ${o.candidate_name}?`)) return;
    try { await api.delete(`/offer-letters/${o.id}`); await load(); } catch (e) { setErr(formatApiError(e)); }
  };

  if (user?.role !== "super_admin") {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <h1 data-testid="exp-access-denied" className="mt-4 font-heading text-2xl font-bold">Access Denied — Admin Only</h1>
        <p className="mt-2 text-sm text-muted-foreground">Experience Letters are restricted to the Super Admin.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6">
        <p className="label-eyebrow text-primary">HR Tools</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Experience Letters</h1>
        <p className="mt-1 text-sm text-muted-foreground">Generate professional experience certificates on your letterhead — print, download and keep history.</p>
      </header>
      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={generate} className="sharp-card grid gap-3 p-5 sm:grid-cols-2 self-start">
          <h2 className="sm:col-span-2 flex items-center gap-2 font-heading text-lg font-semibold"><FileSignature className="h-5 w-5 text-primary" /> New experience letter</h2>
          <div><label className="label-eyebrow mb-1.5 block">Employee name *</label>
            <input data-testid="exp-name" required value={form.candidate_name} onChange={set("candidate_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Employee ID No.</label>
            <input data-testid="exp-empid" value={form.employee_id} onChange={set("employee_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Job title *</label>
            <input data-testid="exp-title" required value={form.job_title} onChange={set("job_title")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">Company *</label>
            <input data-testid="exp-company" required value={form.company_name} onChange={set("company_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">From date *</label>
            <input data-testid="exp-from" required type="date" value={form.start_date} onChange={set("start_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div><label className="label-eyebrow mb-1.5 block">To date *</label>
            <input data-testid="exp-to" required type="date" value={form.end_date} onChange={set("end_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div className="sm:col-span-2"><label className="label-eyebrow mb-1.5 block">Responsibilities / achievements (one per line)</label>
            <textarea data-testid="exp-duties" value={form.perks} onChange={set("perks")} rows={3} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
          <div className="sm:col-span-2 flex justify-end">
            <button data-testid="exp-generate" disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              <Sparkles className="h-4 w-4" /> {busy ? "Generating…" : "Generate letter"}</button>
          </div>
        </form>
        <div className="sharp-card self-start overflow-hidden">
          <div className="border-b border-border/60 bg-secondary/30 px-5 py-3"><h2 className="font-heading text-lg font-semibold">History ({history.length})</h2></div>
          <div className="max-h-[560px] divide-y divide-border/40 overflow-y-auto">
            {history.map((o) => (
              <div key={o.id} data-testid={`exp-row-${o.id}`} className="flex items-center justify-between gap-2 px-5 py-3 hover:bg-secondary/40">
                <div className="min-w-0"><p className="truncate font-medium">{o.candidate_name} — {o.job_title}</p>
                  <p className="text-xs text-muted-foreground">{o.company_name} · {fd(o.created_at)}</p></div>
                <div className="flex shrink-0 gap-1">
                  <button data-testid={`exp-view-${o.id}`} title="View" onClick={() => setPreview({ title: `Experience Letter - ${o.candidate_name}`, html: o.letter_html })} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Eye className="h-3.5 w-3.5" /></button>
                  <button data-testid={`exp-print-${o.id}`} title="Print" onClick={() => printL(o.letter_html, `Experience Letter - ${o.candidate_name}`)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Printer className="h-3.5 w-3.5" /></button>
                  <button data-testid={`exp-download-${o.id}`} title="Download" onClick={() => downloadL(o.letter_html, `Experience Letter - ${o.candidate_name}`)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Download className="h-3.5 w-3.5" /></button>
                  <button data-testid={`exp-delete-${o.id}`} title="Delete" onClick={() => del(o)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
            {history.length === 0 && <p className="px-5 py-10 text-center text-xs text-muted-foreground">No experience letters yet.</p>}
          </div>
        </div>
      </div>
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-3xl sharp-card max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
              <h3 className="font-heading text-lg font-semibold">{preview.title}</h3>
              <div className="flex gap-2">
                <button data-testid="exp-preview-print" onClick={() => printL(preview.html, preview.title)} className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-1.5 text-xs font-semibold hover:bg-secondary"><Printer className="h-3.5 w-3.5" /> Print</button>
                <button data-testid="exp-preview-download" onClick={() => downloadL(preview.html, preview.title)} className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-1.5 text-xs font-semibold hover:bg-secondary"><Download className="h-3.5 w-3.5" /> Download</button>
                <button data-testid="exp-preview-close" onClick={() => setPreview(null)} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="overflow-y-auto bg-white p-8" dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        </div>
      )}
    </div>
  );
}
